/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useEffect, useState } from 'react';
import logger from 'utils/logger';
import { getPrebuiltIntegrationsUrl } from 'utils/prebuiltIntegrationsSource';

/**
 * A pair of applications the catalogue has at least one prebuilt integration
 * for — one chip on the login page.
 *
 * A pair, not an integration: several published integrations connect the same
 * two applications, and a strip that showed each of them would repeat the same
 * two logos with nothing to tell them apart. Pressing a chip picks the pair and
 * leaves choosing between those integrations to the catalogue page, which can
 * name them.
 */
export interface PrebuiltIntegrationFlow {
  /** Stable identity for the pair, independent of the order it is drawn in. */
  key: string;
  /** The two applications, in the order the catalogue first named them. */
  applications: [string, string];
}

interface PrebuiltIntegrationFlowsState {
  flows: PrebuiltIntegrationFlow[];
  isLoading: boolean;
}

/**
 * Anything past this is dropped. The row is a sampler, not the catalogue —
 * that is what Explore integrations next to it is for — and the published list
 * is free to grow without this page turning into a wall of logos.
 */
const MAX_FLOWS = 12;

/**
 * Identity of a pair regardless of which application the catalogue happened to
 * name first, so `Shopify + Slack` and `Slack + Shopify` are one chip.
 */
const pairKey = (applications: string[]): string =>
  [...applications].sort().join('::');

/**
 * Fisher-Yates, on a copy.
 *
 * Shuffled once when the catalogue lands rather than on every render: the row
 * would otherwise reorder itself under the pointer. The published order is the
 * order integrations happened to be added in, which puts the same pairs at the
 * head of the strip — and in front of the cap below — on every visit. A shuffle
 * makes the row a fair sample of the catalogue instead of a window onto its
 * oldest corner.
 */
const shuffle = <T>(items: T[]): T[] => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * The distinct application pairs the published catalogue covers, in a random
 * order. Shuffled before the cap is applied, so every pair has the same chance
 * of making the row rather than the newest ones never appearing.
 */
const toFlows = (rawIntegrations: any[]): PrebuiltIntegrationFlow[] => {
  const flows: PrebuiltIntegrationFlow[] = [];
  const seen = new Set<string>();

  rawIntegrations.forEach((raw) => {
    const applications = raw?.applications;
    // Two logos and an arrow is the whole chip, so an entry that names
    // anything other than a pair has nothing to draw here.
    if (
      !Array.isArray(applications) ||
      applications.length !== 2 ||
      typeof applications[0] !== 'string' ||
      typeof applications[1] !== 'string'
    ) {
      return;
    }

    const key = pairKey(applications);
    if (seen.has(key)) return;
    seen.add(key);
    flows.push({ key, applications: [applications[0], applications[1]] });
  });

  return shuffle(flows).slice(0, MAX_FLOWS);
};

/**
 * The published prebuilt integrations, fetched straight from the catalogue the
 * signed in product reads.
 *
 * Deliberately not `useGetPrebuiltIntegrations`: this runs on the login page,
 * which is mounted outside the query client and the snackbar host that hook
 * needs, and pulling it in would drag the component creation API surface into
 * the unauthenticated bundle. Nothing here is authenticated — the catalogue is
 * a public JSON file — so a bare fetch is the whole requirement.
 *
 * A failure is not surfaced: the row is an invitation, and a landing page that
 * greets a visitor with an error toast about a strip of logos is worse than one
 * that quietly leaves it out.
 */
export const usePrebuiltIntegrationFlows =
  (): PrebuiltIntegrationFlowsState => {
    const [state, setState] = useState<PrebuiltIntegrationFlowsState>({
      flows: [],
      isLoading: true,
    });

    useEffect(() => {
      // Guards against a state write after the visitor has navigated away,
      // which on this page is as easy as pressing Log in.
      const controller = new AbortController();

      const load = async () => {
        try {
          const response = await fetch(getPrebuiltIntegrationsUrl(), {
            signal: controller.signal,
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const rawData = await response.json();
          setState({
            flows: toFlows(rawData?.prebuiltIntegrations ?? []),
            isLoading: false,
          });
        } catch (error) {
          if (controller.signal.aborted) return;
          logger.error('Failed to fetch the prebuilt integrations', error);
          setState({ flows: [], isLoading: false });
        }
      };

      load();
      return () => controller.abort();
    }, []);

    return state;
  };

export default usePrebuiltIntegrationFlows;
